import {selectPluralForm} from '@/services/plural/pluralService';
import type {PluralFormsModel, TimeMeasurementsConfigModel} from '@/types/aem';
import {formatString} from '@/utils/stringUtils';


import {type JustNowConfig, TIME_MULTIPLIERS, TIME_UNITS, type TimeUnit, UNIT_SUFFIX} from './timeConfig';

interface ConfiguredTimeUnit {
    unit: TimeUnit;
    config: PluralFormsModel;
    multiplier: number;
}

const getFormatString = (count: number, forms: PluralFormsModel, locale: string): string => {
    const template = selectPluralForm(count, forms, locale);

    if (template === undefined) return '';

    return formatString(template, count);
};

const getConfiguredUnits = (config: TimeMeasurementsConfigModel): ConfiguredTimeUnit[] => {
    const configuredUnits: ConfiguredTimeUnit[] = [];

    for (const unit of TIME_UNITS) {
        const configKey = `${unit}${UNIT_SUFFIX}` as keyof TimeMeasurementsConfigModel;
        const unitConfig = config[configKey];

        if (!unitConfig) continue;

        const multiplier = TIME_MULTIPLIERS[unit.toUpperCase() as Uppercase<TimeUnit>];

        configuredUnits.push({unit, config: unitConfig, multiplier});
    }

    return configuredUnits;
};

/**
 * Formats a duration into the largest configured unit that divides the rounded
 * duration without remainder. If a larger unit cannot represent the value
 * exactly, or its plural form is missing, the formatter cascades to smaller
 * configured units. Any non-zero value below the smallest configured unit
 * returns an empty string. With `dayPluralForms`/`weekPluralForms`:
 *   < 1 day         → `dayPluralForms.zero`  ("Last day")
 *   1 day           → `dayPluralForms.one`   ("Ends Tomorrow")
 *   3 days          → `dayPluralForms.other` ("3 days left")
 *   7 days          → `weekPluralForms.one`  ("1 week left")
 *   10 days         → `dayPluralForms.other` ("10 days left")
 */
export const formatTime = (
    timeInSeconds: number,
    config: TimeMeasurementsConfigModel & JustNowConfig,
    locale: string,
    floorToLargest = false
): string => {
    const configuredUnits = getConfiguredUnits(config);
    const smallestUnit = configuredUnits[configuredUnits.length - 1];

    if (!smallestUnit) {
        return '';
    }

    const {justNowText, justNowLimit} = config;

    if (justNowText && Number.isFinite(justNowLimit)) {
        const threshold =
            typeof justNowLimit === 'number' ? justNowLimit * smallestUnit.multiplier : smallestUnit.multiplier;

        if (timeInSeconds < threshold) {
            return justNowText;
        }
    }

    const roundedSeconds = Math.round(timeInSeconds);

    if (roundedSeconds <= 0) {
        return getFormatString(0, smallestUnit.config, locale);
    }

    for (const {config: unitConfig, multiplier} of configuredUnits) {
        if (roundedSeconds < multiplier) continue;
        if (!floorToLargest && roundedSeconds % multiplier !== 0) continue;

        const count = floorToLargest ? Math.floor(roundedSeconds / multiplier) : roundedSeconds / multiplier;
        const formatted = getFormatString(count, unitConfig, locale);

        if (formatted) {
            return formatted;
        }
    }

    return '';
};
